import {
  // neutrals
  sage,
  olive,
  sand,
  slate,
  gray,
  mauve,
  // brands
  jade,
  grass,
  green,
  teal,
  blue,
  indigo,
  iris,
  violet,
  plum,
  crimson,
  orange,
  bronze,
  // accents (the earned 10%)
  amber,
  gold,
  // danger
  tomato,
} from '@radix-ui/colors';

type RadixScale = Record<string, string>;

export interface DevPaletteSwatch {
  paper: string;
  ink: string;
  brand: string;
  accent: string;
}

export interface DevPalettePreset {
  id: string;
  name: string;
  vibe: string;
  swatch: DevPaletteSwatch;
  tokens: Record<string, string>;
}

// Radix 12-step scales are ordered 1..12 (light surface → solid → dark text),
// so Object.values gives [step1, …, step12]. Map those onto the app's semantic
// tokens (packages/ui/src/global.css) + the brand/surface scales.
//
// IMPORTANT: the accent maps onto --color-attention* (the app's "earned 10%"
// ochre/money-attention token, consumed by the chốt-sổ nudge, unmatched-payment
// row, attendance "excused" state, etc.). Earlier this wrote dead
// --color-accent-warm* names that nothing consumed, so switching preset only
// moved the brand — the accent never changed. Keep these names in sync with the
// tokens declared in global.css.
function buildTokens(
  neutral: RadixScale,
  brand: RadixScale,
  accent: RadixScale,
  danger: RadixScale,
): Record<string, string> {
  const N = Object.values(neutral);
  const B = Object.values(brand);
  const A = Object.values(accent);
  const D = Object.values(danger);
  const at = (scale: string[], step: number) => scale[step - 1] as string;

  return {
    '--color-background': at(N, 2),
    '--color-foreground': at(N, 12),
    '--color-card': at(N, 1),
    '--color-card-foreground': at(N, 12),
    '--color-popover': at(N, 1),
    '--color-popover-foreground': at(N, 12),
    '--color-primary': at(B, 9),
    '--color-primary-foreground': '#ffffff',
    '--color-secondary': at(N, 3),
    '--color-secondary-foreground': at(N, 12),
    '--color-muted': at(N, 3),
    '--color-muted-foreground': at(N, 11),
    '--color-accent': at(N, 4),
    '--color-accent-foreground': at(N, 12),
    '--color-destructive': at(D, 9),
    '--color-destructive-foreground': '#ffffff',
    '--color-border': at(N, 6),
    '--color-input': at(N, 7),
    '--color-ring': at(B, 8),

    // Attention (ochre/money accent) — the ".impeccable" earned 10%.
    '--color-attention': at(A, 9),
    '--color-attention-foreground': at(A, 12),
    '--color-attention-tint': at(A, 3),

    // Brand scale (brand-500 stays the vivid solid at Radix step 9)
    '--color-brand-50': at(B, 2),
    '--color-brand-100': at(B, 3),
    '--color-brand-200': at(B, 4),
    '--color-brand-300': at(B, 5),
    '--color-brand-400': at(B, 7),
    '--color-brand-500': at(B, 9),
    '--color-brand-600': at(B, 10),
    '--color-brand-700': at(B, 11),
    '--color-brand-800': at(B, 11),
    '--color-brand-900': at(B, 12),

    // Surface scale (0 = lightest paper → 900 = darkest ink)
    '--color-surface-0': at(N, 1),
    '--color-surface-50': at(N, 2),
    '--color-surface-100': at(N, 3),
    '--color-surface-200': at(N, 4),
    '--color-surface-300': at(N, 6),
    '--color-surface-400': at(N, 8),
    '--color-surface-500': at(N, 9),
    '--color-surface-600': at(N, 10),
    '--color-surface-700': at(N, 11),
    '--color-surface-800': at(N, 12),
    '--color-surface-900': at(N, 12),
  };
}

function swatch(neutral: RadixScale, brand: RadixScale, accent: RadixScale): DevPaletteSwatch {
  return {
    paper: Object.values(neutral)[1] as string, // step 2
    ink: Object.values(neutral)[11] as string, // step 12
    brand: Object.values(brand)[8] as string, // step 9
    accent: Object.values(accent)[8] as string, // step 9
  };
}

// Ordered greens first (the shipped direction), then a spread of non-green
// brands so the playground clearly changes the *whole* app, not just a green.
export const devPalettePresets: DevPalettePreset[] = [
  // --- Greens (the "quiet ledger" family) ---
  {
    id: 'sage-jade',
    name: 'Sage & Jade',
    vibe: 'Xanh ngọc — mặc định',
    swatch: swatch(sage, jade, amber),
    tokens: buildTokens(sage, jade, amber, tomato),
  },
  {
    id: 'olive-grass',
    name: 'Olive & Grass',
    vibe: 'Xanh lá ấm, gần gũi',
    swatch: swatch(olive, grass, bronze),
    tokens: buildTokens(olive, grass, bronze, tomato),
  },
  {
    id: 'sand-green',
    name: 'Sand & Green',
    vibe: 'Giấy ấm, xanh cổ điển',
    swatch: swatch(sand, green, amber),
    tokens: buildTokens(sand, green, amber, tomato),
  },
  // --- Blues ---
  {
    id: 'slate-blue',
    name: 'Slate & Blue',
    vibe: 'Xanh dương, tin cậy',
    swatch: swatch(slate, blue, amber),
    tokens: buildTokens(slate, blue, amber, tomato),
  },
  {
    id: 'slate-indigo',
    name: 'Slate & Indigo',
    vibe: 'Chàm sâu, chuyên nghiệp',
    swatch: swatch(slate, indigo, gold),
    tokens: buildTokens(slate, indigo, gold, tomato),
  },
  {
    id: 'gray-teal',
    name: 'Gray & Teal',
    vibe: 'Xanh mòng két, sắc nét',
    swatch: swatch(gray, teal, amber),
    tokens: buildTokens(gray, teal, amber, tomato),
  },
  // --- Violets / purples ---
  {
    id: 'mauve-iris',
    name: 'Mauve & Iris',
    vibe: 'Tím xanh, dịu nhẹ',
    swatch: swatch(mauve, iris, gold),
    tokens: buildTokens(mauve, iris, gold, tomato),
  },
  {
    id: 'mauve-violet',
    name: 'Mauve & Violet',
    vibe: 'Tím sang, hiện đại',
    swatch: swatch(mauve, violet, amber),
    tokens: buildTokens(mauve, violet, amber, tomato),
  },
  {
    id: 'slate-plum',
    name: 'Slate & Plum',
    vibe: 'Mận chín, trầm ấm',
    swatch: swatch(slate, plum, gold),
    tokens: buildTokens(slate, plum, gold, tomato),
  },
  // --- Warm brands ---
  {
    id: 'sand-orange',
    name: 'Sand & Orange',
    vibe: 'Cam ấm, năng lượng',
    swatch: swatch(sand, orange, gold),
    tokens: buildTokens(sand, orange, gold, tomato),
  },
  {
    id: 'sand-bronze',
    name: 'Sand & Bronze',
    vibe: 'Đồng mộc, cổ điển',
    swatch: swatch(sand, bronze, gold),
    tokens: buildTokens(sand, bronze, gold, tomato),
  },
  {
    id: 'gray-crimson',
    name: 'Gray & Crimson',
    vibe: 'Đỏ hồng, nổi bật',
    swatch: swatch(gray, crimson, gold),
    tokens: buildTokens(gray, crimson, gold, tomato),
  },
];
